import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getSitePrices } from "@/lib/pricing.functions";
import { siteTodayKey } from "@/lib/datetime.schedule";
import {
  formatTvCardPrice,
  formatTvCashPrice,
  RANGE_BUCKET_PRICES,
  RANGE_BUCKET_TV,
} from "@/lib/marketing-pricing";
import heroMethod from "@/assets/hero-method.jpg";
import { m } from "@/paraglide/messages.js";
import tvCss from "./tv.css?url";
import roboto400 from "@fontsource/roboto/files/roboto-latin-400-normal.woff2?url";

const TV_VIEWPORT = "width=device-width";

export const Route = createFileRoute("/tv")({
  head: () => ({
    meta: [
      { title: "Range TV Display" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "viewport", content: TV_VIEWPORT },
    ],
    links: [
      {
        rel: "preload",
        href: roboto400,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      { rel: "stylesheet", href: tvCss },
    ],
  }),
  component: TvDisplayPage,
});

function TvDisplayPage() {
  useEffect(() => {
    document.documentElement.classList.add("tv-route");
    document.body.classList.add("tv-route");

    const viewportMetas = document.querySelectorAll('meta[name="viewport"]');
    const viewportMeta = viewportMetas[0] as HTMLMetaElement | undefined;
    const previousViewport = viewportMeta?.content ?? null;
    for (let i = 1; i < viewportMetas.length; i += 1) {
      viewportMetas[i].remove();
    }
    if (viewportMeta) {
      viewportMeta.content = TV_VIEWPORT;
    }

    return () => {
      document.documentElement.classList.remove("tv-route");
      document.body.classList.remove("tv-route");
      if (viewportMeta && previousViewport !== null) {
        viewportMeta.content = previousViewport;
      }
    };
  }, []);

  const pricesQ = useQuery({
    queryKey: ["site-prices"],
    queryFn: () => getSitePrices(),
    refetchInterval: 60_000,
  });

  const fromDb = pricesQ.data ?? [];
  const rangeBuckets =
    fromDb.length > 0
      ? fromDb
          .filter((p) => p.category === "range_bucket")
          .map((p) => ({
            label: p.label,
            cashCents: p.cash_cents,
            cardCents: p.card_cents,
          }))
      : RANGE_BUCKET_PRICES;

  const seasonYear = Number(siteTodayKey().slice(0, 4));

  return (
    <div className="tv-display">
      <div className="tv-display-inner">
        <div className="tv-display-backdrop" aria-hidden>
          <img src={heroMethod} alt="" width={1920} height={1080} className="tv-display-bg" />
        </div>
        <section>
          <h1>{seasonYear} Season</h1>
          <table className="pricing">
            <thead>
              <tr>
                <th>Size</th>
                <th>Balls</th>
                <th>Cash</th>
                <th>Card</th>
              </tr>
            </thead>
            <tbody>
              {rangeBuckets.map((bucket) => {
                const tvMeta = RANGE_BUCKET_TV[bucket.label] ?? {
                  size: bucket.label.replace(/ Bucket$/, ""),
                  balls: null as number | null,
                };

                return (
                  <tr key={bucket.label}>
                    <td>{tvMeta.size}</td>
                    <td>{tvMeta.balls ?? "—"}</td>
                    <td>{formatTvCashPrice(bucket.cashCents)}</td>
                    <td>{formatTvCardPrice(bucket.cardCents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <section>
          <h1>Golf Lessons</h1>
          <table className="lessons">
            <tbody>
              <tr>
                <td>
                  Ask about individual or group lessons with our 7 time PGA Teacher of the Year
                  award recipient, and PGA Professional
                  <br />
                  Mark Egly
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <footer>
          <img
            src="/tv/images/powered-by-top-tracer-blue.png"
            alt={m.tv_toptracer_logo_alt()}
            className="top-tracer-logo"
          />
        </footer>
      </div>
    </div>
  );
}
